import { useState } from 'react'
import type { TerminalLine } from '@/lib/types'

/**
 * Snapshot exporter — converts recent terminal output to markdown and
 * drops it on the clipboard for pasting into Slack/Notion/tickets.
 *
 * Takes the last 200 lines, strips §<c>text§ colour markup, groups
 * commands + their output as separate code blocks, prefixes system
 * messages with ">". No backend round-trip.
 */

const MAX_LINES = 200

function stripColorMarkup(raw: string): string {
  return raw.replace(/§([grabcdep])([^§]+)§/g, '$2')
}

function buildMarkdown(history: TerminalLine[]): string {
  const slice = history.slice(-MAX_LINES)
  const ts = new Date().toLocaleString('en-GB', { hour12: false })

  const blocks: string[] = [
    '# AgentVault Terminal Snapshot',
    '',
    `_Exported ${ts} — ${slice.length} lines_`,
    '',
  ]

  // Walk lines, group by command boundaries into fenced code blocks so
  // the output renders as pasteable terminal sessions rather than prose.
  let buffer: string[] = []
  const flush = () => {
    if (buffer.length === 0) return
    blocks.push('```')
    blocks.push(...buffer)
    blocks.push('```')
    blocks.push('')
    buffer = []
  }

  for (const line of slice) {
    const text = stripColorMarkup(line.content)
    if (line.type === 'command') {
      // Start fresh block for each command so the render is clean
      flush()
      buffer.push(`[${line.timestamp}] > ${text}`)
    } else if (line.type === 'system') {
      buffer.push(`[${line.timestamp}] SYS | ${text}`)
    } else if (line.type === 'error') {
      buffer.push(`[${line.timestamp}] ! ${text}`)
    } else {
      // quote / table / chart / dashboard / text — just append
      for (const row of text.split('\n')) buffer.push(row)
    }
  }
  flush()

  return blocks.join('\n')
}

export function SnapshotButton({ history }: { history: TerminalLine[] }) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'error'>('idle')

  const handleClick = async () => {
    const md = buildMarkdown(history)
    try {
      await navigator.clipboard.writeText(md)
      setStatus('copied')
      setTimeout(() => setStatus('idle'), 1800)
    } catch {
      // Clipboard blocked — surface a failure hint and dump to console
      // so the user can grab it from devtools as a fallback.
      console.log('--- AgentVault Terminal Snapshot ---\n' + md)
      setStatus('error')
      setTimeout(() => setStatus('idle'), 1800)
    }
  }

  const label =
    status === 'copied' ? 'Copied!' : status === 'error' ? 'See console' : 'Snapshot'

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={history.length === 0}
      className="text-crt-dim hover:text-crt-green disabled:opacity-40 disabled:hover:text-crt-dim font-mono text-xs transition-colors flex items-center gap-1"
      title={`Copy last ${MAX_LINES} lines as markdown`}
    >
      <kbd className="text-[10px] px-1 py-0.5 border border-crt-dim/40 rounded">↗</kbd>
      <span>{label}</span>
    </button>
  )
}
